import { formatCapitalize } from "./formatCapitalize.js";

const hasMeaningfulTime = (value) => {
  if (value === null || value === undefined) return false;
  if (typeof value === "string" && value.trim() === "") return false;
  return true;
};

const normalizeChildStatus = (status) =>
  String(status ?? "")
    .toLowerCase()
    .replace(/[_\s-]/g, "");

export const getChildActualDropOffTime = (child) =>
  child?.actualDropOffTime ?? child?.actualDropoffTime ?? null;

export const isChildDroppedOff = (child) => {
  if (hasMeaningfulTime(getChildActualDropOffTime(child))) {
    return true;
  }

  const status = normalizeChildStatus(child?.status);
  return status === "dropoff";
};

export const isChildStillOnboard = (child) => !isChildDroppedOff(child);

export const normalizeDispatchOccupancy = (occupancy, tripStatus) => {
  if (!occupancy || typeof occupancy !== "object") {
    return occupancy;
  }

  const status = String(tripStatus ?? "").toLowerCase();

  if (status === "completed") {
    return {
      ...occupancy,
      currentOccupancy: 0,
      childrenOnboard: [],
    };
  }

  const rawChildren = Array.isArray(occupancy.childrenOnboard)
    ? occupancy.childrenOnboard
    : [];
  const childrenOnboard = rawChildren.filter(isChildStillOnboard);
  const currentOccupancy = childrenOnboard.length;

  return {
    ...occupancy,
    currentOccupancy,
    childrenOnboard,
  };
};

export const formatDispatchTripStatus = (status) => {
  const normalized = String(status ?? "").toLowerCase();
  switch (normalized) {
    case "ongoing":
      return "Ongoing";
    case "completed":
      return "Completed";
    case "at-final-stop":
      return "At Final Stop";
    default:
      if (!status) return "Unknown";
      return formatCapitalize(String(status).replace(/-/g, " "));
  }
};

export const isCompletedDispatchTrip = (row) =>
  String(row?.status ?? "").toLowerCase() === "completed";

export const filterDispatchBoardTrips = (items = []) =>
  items.filter((item) => !isCompletedDispatchTrip(item));

export const getDispatchTripStatusBadgeProps = (status) => {
  const normalized = String(status ?? "").toLowerCase();
  switch (normalized) {
    case "ongoing":
      return { background: "#EFF8FF", color: "#175CD3" };
    case "completed":
      return { background: "#ECFDF3", color: "#027A48" };
    case "at-final-stop":
      return { background: "#FFFAEB", color: "#B54708" };
    default:
      return { background: "#F2F4F7", color: "#344054" };
  }
};

export const getDispatchVehicleLabel = (vehicleDetails) => {
  if (!vehicleDetails) return undefined;
  return vehicleDetails.busNumber
    ? `${vehicleDetails.vehicleNumber} (${vehicleDetails.busNumber})`
    : vehicleDetails.vehicleNumber;
};

export const hasDispatchOnboardStudents = (row) => {
  const normalized = normalizeDispatchOccupancy(row?.occupancy, row?.status);
  return (normalized?.childrenOnboard?.length ?? 0) > 0;
};

export const buildDispatchOccupancyModalData = (row) => {
  if (!row) return null;

  const normalized = normalizeDispatchOccupancy(row.occupancy, row.status);
  const childrenOnboard = normalized?.childrenOnboard || [];

  return {
    childrenOnboard,
    occupancyLabel: `${normalized?.currentOccupancy ?? 0}/${normalized?.maxCapacity ?? 0}`,
    tripName: row.tripName,
    vehicleLabel: getDispatchVehicleLabel(row.vehicleDetails),
  };
};

const getOnboardChildIdentity = (child) =>
  [
    child?.childId ?? "",
    child?.name ?? "",
    child?.preferredName ?? "",
    child?.status ?? "",
    getChildActualDropOffTime(child) ?? "",
  ].join("\0");

export const areDispatchOnboardChildrenEqual = (left = [], right = []) => {
  if (left.length !== right.length) return false;
  for (let index = 0; index < left.length; index += 1) {
    if (getOnboardChildIdentity(left[index]) !== getOnboardChildIdentity(right[index])) {
      return false;
    }
  }
  return true;
};

export const areDispatchOccupancyModalDataEqual = (left, right) => {
  if (!left || !right) return left === right;
  return (
    left.occupancyLabel === right.occupancyLabel &&
    left.tripName === right.tripName &&
    left.vehicleLabel === right.vehicleLabel &&
    areDispatchOnboardChildrenEqual(left.childrenOnboard, right.childrenOnboard)
  );
};

export const normalizeDispatchVehicleRecord = (record) => {
  if (!record || typeof record !== "object") {
    return record;
  }

  return {
    ...record,
    occupancy: normalizeDispatchOccupancy(record.occupancy, record.status),
  };
};
